import {
  AppBar,
  Avatar,
  Box,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  IconButton,
  useTheme,
  Divider,
  Badge,
  Tooltip,
  alpha,
} from "@mui/material";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

const Appbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClickLogout = () => {
    setAnchorEl(null);
    localStorage.removeItem("token");
    localStorage.removeItem("type");
    localStorage.removeItem("userId");
    localStorage.removeItem("userType");
    navigate("/login", { replace: true });
  };

  // Get current page title from path
  const getPageTitle = () => {
    const path = location.pathname.split("/")[1] || "dashboard";
    const titles: Record<string, string> = {
      dashboard: "Dashboard",
      patients: "Clients",
      therapists: "Clinicians",
      sessions: "Sessions",
      payouts: "Payouts",
      reports: "Reports",
      resources: "Resources",
      services: "Services",
      symptoms: "Symptoms",
      pricing: "Pricing",
      blog: "Blog",
      analytics: "Analytics",
      translations: "Translations",
      tickets: "Support Tickets",
      users: "Admin Users",
      settings: "Settings",
      "system-settings": "System Settings",
      "app-config": "App Configuration",
      "medical-records": "Medical Records",
      "notifications-management": "Notifications",
      "clinician-approval": "Clinician Approval",
      "specialist-registrations": "Specialist Registrations",
      "user-analytics": "User Analytics",
      "payments-dashboard": "Payments",
      "support-tickets": "Support",
    };
    return titles[path] || path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, " ");
  };

  return (
    <AppBar
      color="inherit"
      position="fixed"
      elevation={0}
      sx={{
        height: 72,
        backgroundColor: "rgba(255, 255, 255, 0.85)",
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
        ml: "272px",
        width: "calc(100% - 272px)",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "100%",
          minHeight: "72px !important",
          // Theme sets MuiToolbar padding: 0 !important — override so title isn't flush to sidebar
          px: "32px !important",
        }}
      >
        {/* Left side - Page title */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#0F172A",
              fontSize: "20px",
              letterSpacing: "-0.02em",
            }}
          >
            {getPageTitle()}
          </Typography>
        </Box>

        {/* Right side - Actions */}
        <Box display="flex" alignItems="center" gap={0.5}>
          {/* Search Button */}
          <Tooltip title="Search" arrow>
            <IconButton
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                color: "#64748B",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                },
              }}
            >
              <SearchRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notifications" arrow>
            <IconButton
              onClick={() => navigate("/notifications-management")}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                color: "#64748B",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                },
              }}
            >
              <Badge
                variant="dot"
                color="error"
                sx={{
                  "& .MuiBadge-dot": {
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    border: "2px solid white",
                  },
                }}
              >
                <NotificationsNoneRoundedIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Settings */}
          <Tooltip title="Settings" arrow>
            <IconButton
              onClick={() => navigate("/system-settings")}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                color: "#64748B",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                },
              }}
            >
              <SettingsOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Divider */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 1,
              borderColor: "rgba(226, 232, 240, 0.8)",
              height: 32,
              alignSelf: "center",
            }}
          />

          {/* Profile */}
          <Box
            onClick={handleMenu}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              borderRadius: "12px",
              py: 0.5,
              px: 1,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.03)",
              },
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background:
                  "linear-gradient(135deg, #D58E4E 0%, #E9A767 100%)",
                fontSize: 14,
                fontWeight: 700,
                boxShadow: "0 2px 8px rgba(213, 142, 78, 0.3)",
              }}
            >
              A
            </Avatar>
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#1E293B",
                  lineHeight: 1.3,
                }}
              >
                Admin
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#94A3B8",
                  lineHeight: 1.2,
                }}
              >
                Super Admin
              </Typography>
            </Box>
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: 18,
                color: "#94A3B8",
                transition: "transform 0.2s ease",
                transform: anchorEl ? "rotate(180deg)" : "none",
              }}
            />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: "12px",
                boxShadow:
                  "0 20px 60px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05)",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                overflow: "visible",
                "&::before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 20,
                  width: 10,
                  height: 10,
                  bgcolor: "white",
                  transform: "translateY(-50%) rotate(45deg)",
                  borderLeft: "1px solid rgba(226, 232, 240, 0.8)",
                  borderTop: "1px solid rgba(226, 232, 240, 0.8)",
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, mb: 0.5 }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}
              >
                Admin User
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>
                admin@talaqa.com
              </Typography>
            </Box>
            <Divider sx={{ mb: 0.5 }} />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate("/system-settings");
              }}
              sx={{
                py: 1.25,
                px: 2,
                borderRadius: "8px",
                mx: 0.75,
                mb: 0.25,
                fontSize: "13px",
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: "#F8FAFC",
                },
              }}
            >
              <SettingsOutlinedIcon
                sx={{ mr: 1.5, fontSize: 18, color: "#64748B" }}
              />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Settings
              </Typography>
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={handleClickLogout}
              sx={{
                py: 1.25,
                px: 2,
                borderRadius: "8px",
                mx: 0.75,
                mb: 0.5,
                fontSize: "13px",
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: "#FEF2F2",
                },
              }}
            >
              <LogoutIcon
                sx={{
                  mr: 1.5,
                  fontSize: 18,
                  color: "#EF4444",
                }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "#EF4444" }}
              >
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Appbar;
